// Pull the completed elements of a named top-level array out of a
// partially-streamed JSON document. Used to progressively render the streaming
// AI proposals: the forced-tool input JSON accumulates chunk by chunk, and each
// array element (a proposed cluster / capability / assumption) becomes
// renderable the moment its object closes — per-item grain, no token-level
// parsing. The buffer is always a prefix of a well-formed document (Bedrock
// validates the tool schema before anything streams), truncated anywhere —
// including mid-string or mid-escape.

// Index just past the closing quote of the string starting at raw[start]
// (which must be '"'), or -1 if the buffer ends before the string closes.
function scanString(raw: string, start: number): number {
  let escaped = false;
  for (let i = start + 1; i < raw.length; i++) {
    if (escaped) escaped = false;
    else if (raw[i] === '\\') escaped = true;
    else if (raw[i] === '"') return i + 1;
  }
  return -1;
}

// Index just past the value (object or array) starting at raw[start], or -1 if
// the buffer ends before it closes. String-aware, so structural characters
// inside string values don't count.
function scanBalanced(raw: string, start: number): number {
  let depth = 0;
  let i = start;
  while (i < raw.length) {
    const ch = raw[i];
    if (ch === '"') {
      const end = scanString(raw, i);
      if (end < 0) return -1;
      i = end;
      continue;
    }
    if (ch === '{' || ch === '[') depth++;
    else if (ch === '}' || ch === ']') {
      depth--;
      if (depth === 0) return i + 1;
    }
    i++;
  }
  return -1;
}

function skipWhitespace(raw: string, start: number): number {
  let i = start;
  while (i < raw.length && /\s/.test(raw[i])) i++;
  return i;
}

// Find the start of the `[` for `"key": [` at the top level of the root
// object, or -1. Tracks depth and strings from the start of the document so a
// nested key or the key as a string value can't match.
function findTopLevelArray(raw: string, key: string): number {
  const token = `"${key}"`;
  let depth = 0;
  let i = 0;
  while (i < raw.length) {
    const ch = raw[i];
    if (ch === '"') {
      const end = scanString(raw, i);
      if (end < 0) return -1; // buffer ends inside a string
      if (depth === 1 && raw.slice(i, end) === token) {
        const colon = skipWhitespace(raw, end);
        if (raw[colon] === ':') {
          const value = skipWhitespace(raw, colon + 1);
          if (raw[value] === '[') return value;
        }
      }
      i = end;
      continue;
    }
    if (ch === '{' || ch === '[') depth++;
    else if (ch === '}' || ch === ']') depth--;
    i++;
  }
  return -1;
}

export function completedArrayItems(raw: string, key: string): unknown[] {
  const arrayStart = findTopLevelArray(raw, key);
  if (arrayStart < 0) return [];

  const items: unknown[] = [];
  let i = arrayStart + 1;
  while (i < raw.length) {
    i = skipWhitespace(raw, i);
    const ch = raw[i];
    if (ch === ',') {
      i++;
      continue;
    }
    if (ch === '{') {
      const end = scanBalanced(raw, i);
      if (end < 0) break; // element still streaming
      try {
        items.push(JSON.parse(raw.slice(i, end)));
      } catch {
        break; // shouldn't happen on a valid prefix — stop rather than mis-render
      }
      i = end;
      continue;
    }
    // `]` (array closed), end of buffer, or a non-object element (not in any
    // of our tool schemas) — either way, nothing more to collect.
    break;
  }
  return items;
}

// The `title` of the trailing INCOMPLETE element of the named top-level array,
// or null if there is no element mid-stream / its title hasn't closed yet.
// `title` is the first field in all three generation tool schemas, so it is
// usually available seconds before the element completes — this powers the
// "drafting…" ghost row while completedArrayItems still excludes the element.
export function draftingItemTitle(raw: string, key: string): string | null {
  const arrayStart = findTopLevelArray(raw, key);
  if (arrayStart < 0) return null;

  let i = arrayStart + 1;
  while (i < raw.length) {
    i = skipWhitespace(raw, i);
    const ch = raw[i];
    if (ch === ',') {
      i++;
      continue;
    }
    if (ch === '{') {
      const end = scanBalanced(raw, i);
      if (end >= 0) {
        i = end; // completed element — keep walking
        continue;
      }
      return titleWithinElement(raw, i);
    }
    break; // `]`, end of buffer, or a non-object element
  }
  return null;
}

// Scan an incomplete element (starting at its `{`) for a `"title"` key at the
// element's own depth, and return its string value if that string has closed.
function titleWithinElement(raw: string, elementStart: number): string | null {
  let depth = 0;
  let i = elementStart;
  while (i < raw.length) {
    const ch = raw[i];
    if (ch === '"') {
      const end = scanString(raw, i);
      if (end < 0) return null; // buffer ends inside a string
      if (depth === 1 && raw.slice(i, end) === '"title"') {
        const colon = skipWhitespace(raw, end);
        if (raw[colon] === ':') {
          const valueStart = skipWhitespace(raw, colon + 1);
          if (raw[valueStart] === '"') {
            const valueEnd = scanString(raw, valueStart);
            if (valueEnd < 0) return null; // title still streaming
            try {
              return JSON.parse(raw.slice(valueStart, valueEnd)) as string;
            } catch {
              return null;
            }
          }
        }
      }
      i = end;
      continue;
    }
    if (ch === '{' || ch === '[') depth++;
    else if (ch === '}' || ch === ']') depth--;
    i++;
  }
  return null;
}
